import { Formik } from 'formik';

import MevoInput from 'src/components/Input';
import Typography from 'src/components/Typography';
import { Box } from 'src/components/UI/Box';
import { FormControl } from 'src/components/UI/FormControl';
import { MASKS } from 'src/components/UI/Input/MaskedInput';

import { Yup } from 'src/helpers/Yup';
import { validateCPF } from 'src/helpers/cpf';
import { onlyDigits } from 'src/helpers/string';
import { removeSpacesAndSpecialChars } from 'src/helpers/removeSpacesAndSpecialChars';

import { Flex } from 'src/components/UI/Flex';
import { DateFromFormat, DateFromIso, validateDateFromBrazilianForm } from 'src/helpers/Date';
import { isEmpty } from 'lodash';

import { Button } from 'src/components/UI/Button';
import { useAssistantContext } from 'src/providers/AssistantProvider';
import { useSearchParams } from 'react-router-dom';
import { TabsHeader } from './TabsHeader';
import { TABS_INDEX } from './AssistantSignUp';

const PersonalDataSchema = Yup.object().shape({
  fullName: Yup.string().required('Informe seu nome completo.'),
  cpf: Yup.string()
    .required('Informe um número de CPF válido.')
    .test('validate cpf', 'Informe um número de CPF válido.', function (cpf) {
      return validateCPF(cpf || '');
    }),
  email: Yup.string().email('Informe um email válido.').required('Informe um email válido.'),
  cellphoneNumber: Yup.string()
    .required('Informe um número de celular válido.')
    .test('validate phoneNumber', 'Informe um número de celular válido.', function (cellphoneNumber) {
      if (!cellphoneNumber) return false;
      return onlyDigits(cellphoneNumber).length === 11;
    }),
  birthdate: Yup.string()
    .required('Informe uma data de nascimento válida.')
    .test('match', 'Informe uma data de nascimento válida.', (birthdate) => {
      return validateDateFromBrazilianForm(birthdate);
    }),
});

interface PersonalDataProps {
  onNext(): void;
  tabClick(index: number): void;
}

export function PersonalData(props: PersonalDataProps) {
  const { updateCreateAssistantFormData } = useAssistantContext();

  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  return (
    <Formik
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      initialValues={{
        fullName: '',
        cpf: '',
        email,
        cellphoneNumber: '',
        birthdate: '',
      }}
      validationSchema={PersonalDataSchema}
      onSubmit={async (values, formik) => {
        const date = DateFromFormat(values.birthdate || '', 'dd/MM/yyyy');

        const payloadData = {
          ...values,
          fullName: removeSpacesAndSpecialChars(values.fullName),
          cpf: onlyDigits(values.cpf),
          cellphoneNumber: onlyDigits(values.cellphoneNumber),
          birthdate: DateFromIso(date),
        };

        props.onNext();
        updateCreateAssistantFormData(payloadData);
        formik.setSubmitting(false);
      }}
    >
      {(formikProps) => {
        const submit = async () => {
          const errors = await formikProps.validateForm();

          if (isEmpty(errors)) {
            return formikProps.submitForm();
          }
        };

        return (
          <form
            name={'personal-data'}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            style={{ width: '100%' }}
          >
            <Box>
              <Box mb={'20px'}>
                <Typography model="h2" weight="bold" textAlign="center">
                  Bem vinda(o)
                </Typography>
                <Typography model="h3" weight="regular" textAlign="center">
                  Complete seu cadastro
                </Typography>
              </Box>
              <TabsHeader
                tabClick={props.tabClick}
                currentIndex={TABS_INDEX.PERSONAL_DATA}
                isFirstStepCompleted={false}
              />
              <Box>
                <FormControl isInvalid={!!formikProps.errors.email} mb={'20px'}>
                  <MevoInput
                    id="email"
                    labelText="EMAIL*"
                    h={'45px'}
                    placeholderText={'Digite seu email'}
                    name={'email'}
                    type={'email'}
                    value={formikProps.values.email}
                    onChange={formikProps.handleChange}
                    onBlur={() => formikProps.validateField('email')}
                    errorText={formikProps.errors.email}
                    isReadOnly
                  />
                </FormControl>
                <FormControl isInvalid={!!formikProps.errors.fullName} mb={'20px'}>
                  <MevoInput
                    id="fullName"
                    labelText="NOME COMPLETO*"
                    h={'45px'}
                    placeholderText={'Digite seu nome'}
                    name={'fullName'}
                    value={formikProps.values.fullName}
                    onChange={formikProps.handleChange}
                    onBlur={() => formikProps.validateField('fullName')}
                    autoFocus
                    errorText={formikProps.errors.fullName}
                  />
                </FormControl>

                <Flex gap={'20px'}>
                  <FormControl isInvalid={!!formikProps.errors.birthdate} mb={'20px'}>
                    <MevoInput
                      labelText="DATA DE NASCIMENTO*"
                      type={'tel'}
                      h={'45px'}
                      placeholder={'00/00/0000'}
                      name={'birthdate'}
                      value={formikProps.values.birthdate}
                      onChange={formikProps.handleChange}
                      onBlur={() => formikProps.validateField('birthdate')}
                      errorText={formikProps.errors.birthdate}
                      mask={MASKS.BIRTH_DATE}
                    />
                  </FormControl>
                  <FormControl isInvalid={!!formikProps.errors.cpf} mb={'20px'}>
                    <MevoInput
                      id="cpf"
                      labelText="CPF*"
                      type={'tel'}
                      h={'45px'}
                      placeholder={'000.000.000-00'}
                      name={'cpf'}
                      value={formikProps.values.cpf}
                      onChange={formikProps.handleChange}
                      onBlur={() => formikProps.validateField('cpf')}
                      errorText={formikProps.errors.cpf}
                      mask={MASKS.CPF}
                    />
                  </FormControl>
                </Flex>

                <FormControl isInvalid={!!formikProps.errors.cellphoneNumber} mb={{ base: 0, sm: '16px' }}>
                  <MevoInput
                    id="cellphoneNumber"
                    labelText="Celular*"
                    type={'tel'}
                    h={'45px'}
                    placeholder={'(99) 9 9999-9999'}
                    name={'cellphoneNumber'}
                    value={formikProps.values.cellphoneNumber}
                    onChange={formikProps.handleChange}
                    onBlur={() => formikProps.validateField('cellphoneNumber')}
                    errorText={formikProps.errors.cellphoneNumber}
                    mask={MASKS.BRAZIL_PHONE}
                  />
                </FormControl>

                <Button h={'60px'} w={'100%'} mb={'22px'} type={'submit'}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
}
